import { MediaMatcher } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { Router } from '@angular/router';
import { PaymentMethod, PaymentMethods, PaymentMethodsType } from '@cargos/sprintpay-models';
import { AdvancedFlowApiService, HideDownloadDirective } from '@cargos/sprintpay-ui';
import { Subject, takeUntil } from 'rxjs';
import { CartBillService, PaymentMethodsService, SessionService } from 'src/app/shared/services';
import { PaymentMethodSelected } from 'src/app/shared/services/summary/models/types';
import { PaymentMethodsHandlerService } from '../../shared/services/payment-methods-handler.service';
import { AuthenticatedPaymentMethodComponent } from '../authenticated-payment-method/authenticated-payment-method.component';
import { CargoCreditComponent } from '../cargo-credit/cargo-credit.component';
import { CreditCardsListComponent } from '../credit-card-list/credit-cards.component';
import { EcheckListComponent } from '../echecks-list/echeck.component';
import { FinanceCreditComponent } from '../finance-credit/finance.component';
import { PaypalComponent } from '../paypal/paypal.component';
import { SprintpayFlexComponent } from '../sprintpay-flex/sprintpay-flex.component';
//Todo: tercero
@Component({
    standalone: true,
    selector: 'app-cart-payment-methods',
    templateUrl: './cart-payment-methods.html',
    styleUrl: './cart-payment-methods.scss',
    imports: [
        CommonModule,
        MatRadioModule,
        MatListModule,
        MatExpansionModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatDividerModule,
        AuthenticatedPaymentMethodComponent,
        CreditCardsListComponent,
        EcheckListComponent,
        CargoCreditComponent,
        FinanceCreditComponent,
        PaypalComponent,
        MatButtonModule,
        SprintpayFlexComponent,
        HideDownloadDirective,
    ],
})
export class CartPaymentMethodsTempComponent implements OnInit, OnDestroy {
    public paymentMethodSelected: PaymentMethodSelected | null = null;
    public paymentMethodsList: PaymentMethod[] = [];
    public methodsForThreshold: PaymentMethod[] = [];
    public isSelectingPaymentMethod = false;
    public expandedPanel: boolean;
    public isGuest: boolean;
    public readonly PaymentMethods = PaymentMethods;
    public isAuthenticated: boolean = false;
    private unsubscribe$: Subject<void> = new Subject<void>();
    public isCartAboveThreshold: boolean = false;
    public isEnableSomePaymentMethod = false;
    public isFinanceEnabled = false;
    private _mobileQueryListener: () => void;
    public mobileQuery: MediaQueryList;

    private readonly advancedFlowApiService = inject(AdvancedFlowApiService);

    /**
     * This variable does not indicate whether the cart is above the threshold.
     * Instead, it serves as a configuration to determine the selected payment method based on the section.
     */
    @Input() aboveThreshold: boolean = false;

    constructor(
        private router: Router,
        private paymentMethodsService: PaymentMethodsService,
        private cartBillService: CartBillService,
        private sessionService: SessionService,
        private matDialog: MatDialog,
        private changeDetectorRef: ChangeDetectorRef,
        private media: MediaMatcher,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService
    ) {
        this.isGuest = !this.sessionService.isAuthenticated();
        this.expandedPanel = this.isGuest;
        this.mobileQuery = this.media.matchMedia('(max-width: 767px)');
        this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    }

    ngOnInit(): void {
        this.subscribeGetDefaultPaymentMethodAndCart();
        this.subscribePaymentMethods();
        this.subscribeToDefaultPaymentMethod();
        this.subscribeSessionStatus();
        this.subscribeIsCartAboveThreshold();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    /**
     * @method subscribePaymentMethods()
     * @description Subscribes to the payment methods observable and updates the state to determine
     *              if the "Change Payment Method" button should be available for guest users.
     */
    private subscribePaymentMethods(): void {
        this.paymentMethodsService
            .getFilteredPaymentMethods$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: ({ methods, methodsForThreshold }) => {
                    this.paymentMethodsList = methods || [];
                    this.methodsForThreshold = methodsForThreshold || [];
                    this.hasFinancePaymentMethod();
                },
            });
    }

    /**
     * @method subscribeGetDefaultPaymentMethodAndCart()
     * @description Preselect payment method
     */
    private subscribeGetDefaultPaymentMethodAndCart(): void {
        this.isSelectingPaymentMethod = true;

        this.paymentMethodsHandlerService
            .getPaymentMethodSelected$(this.aboveThreshold)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (selectedPayment: PaymentMethodSelected | null) => {
                    this.isSelectingPaymentMethod = false;
                    if (selectedPayment) {
                        this.isEnableSomePaymentMethod = true;
                        this.paymentMethodSelected = selectedPayment;
                    }
                },
                error: () => {
                    this.isSelectingPaymentMethod = false;
                },
            });
    }

    /**
     * @method selectPaymentMethod()
     * @param (paymentAccount: any) Object in array
     * @param (method: keyof typeof PaymentMethods) Method of payment
     * @description: Triggers an observable in the SUMMARY.ts that handles the logic to change the payment summary in relation to the desired payment method
     */

    selectPaymentMethod(method: PaymentMethodsType, token?: string): void {
        if (this.isAuthenticated) {
            this.expandedPanel = false;
        }
        if (this.aboveThreshold) {
            this.paymentMethodsHandlerService.setPaymentMethodSelectedAboveThreshold({
                method,
                token,
            });
        } else {
            this.paymentMethodsHandlerService.setPaymentMethodSelected({ method, token });
        }
    }

    togglePanel(): void {
        this.expandedPanel = !this.expandedPanel;
    }

    subscribeToDefaultPaymentMethod(): void {
        this.paymentMethodsHandlerService
            .isSettingDefaultPaymentMethod$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isSettingDefaultPaymentMethod: boolean) => {
                    this.isSelectingPaymentMethod = isSettingDefaultPaymentMethod;
                },
                error: () => {
                    this.isSelectingPaymentMethod = false;
                },
            });
    }

    subscribeSessionStatus(): void {
        this.sessionService
            .isAuthenticated$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isAuthenticated: boolean) => {
                    this.isAuthenticated = isAuthenticated;
                },
            });
    }

    subscribeIsCartAboveThreshold(): void {
        this.cartBillService
            .isCartAboveThreshold$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isCartAboveThreshold: boolean) => {
                this.isCartAboveThreshold = isCartAboveThreshold;
            });
    }

    redirectToCreditCard(): void {
        this.advancedFlowApiService.setPaymentMethod('Cards');
        this.router.navigate(['/cart/credit-card']);
    }

    redirectToEcheck(): void {
        this.router.navigate(['/cart/echeck-user']);
    }

    subscribeMobileSize(): void {
        this.mobileQuery.addEventListener('change', this._mobileQueryListener);
    }

    hasFinancePaymentMethod(): void {
        this.isFinanceEnabled = this.paymentMethodsService.isPaymentMethodAvailable(PaymentMethods.SPRINTPAY_FLEX);
    }
}
